import { formatDateOnly, type DateFormat } from '../settings/dateFormat';
import { US_STATE_NAMES } from '../records/taxExemption';
import { t } from '../strings';
import { taxExemptBadges, type TaxExemptBadge } from './taxExemptBadge';

/**
 * 🔒 One Tax Exempt certificate as the Site page's fact card draws it (piece 13, the
 * certificates tile; T3082, decision 7440), the website's and the phone's alike. The
 * server sends the row as it always did, with the facts in `raw`; the words and the
 * rule for which card is muted and which wears the gold badge are made here, once.
 */
export interface CertificateCardRow {
  /** The row's pill: Ended, Awaiting review or Expired; none on a live certificate. */
  pill?: string | null;
  pillGroup?: 'good' | 'warning' | 'error' | 'inactive' | null;
  /** The instant the certificate was ended, where it was. */
  at?: string | null;
  raw?: Record<string, unknown> | null;
}

export interface CertificateCardFact {
  label: string;
  value: string;
  /** The fact is long and stands on a line of its own: the reason, and who ended the certificate and when. */
  wide?: boolean;
}

export interface CertificateCard {
  /** The state written out with its code, and the municipality where the certificate holds one: `Pennsylvania (PA)`. */
  title: string;
  /** The gold badge of the certificate; none on a muted card. */
  badge: TaxExemptBadge | null;
  pill: { text: string; group: 'good' | 'warning' | 'error' | 'inactive' } | null;
  /** An ended, expired or awaiting-review card reads muted. */
  muted: boolean;
  ended: boolean;
  /** The label-and-value grid: Reason, Form, In force from, Expires, and Ended on an ended card. */
  facts: CertificateCardFact[];
}

const text = (value: unknown): string | null => (typeof value === 'string' && value !== '' ? value : null);

/**
 * The card for one certificate row. `when` draws an instant in the viewer's own zone, which differs by app, so the
 * caller hands it in; every date that is a calendar day reads in the Organization's format.
 */
export function certificateCard(row: CertificateCardRow, format: DateFormat, when: (instant: string) => string): CertificateCard {
  const raw = row.raw ?? {};
  const code = text(raw.stateCode) ?? '';
  const stateName = US_STATE_NAMES[code];
  const place = stateName ? `${stateName} (${code})` : code;
  const municipality = text(raw.municipality);
  const effectiveFrom = text(raw.effectiveFrom);
  const expiresOn = text(raw.expiresOn);
  // 🔒 The server states where the certificate stands through the one shared rule (certificateState): an end that lies in the future is not an end, and the badge is drawn for exactly the certificates in force (decisions 7390, 7440).
  const state = text(raw.state);
  const ended = state === 'ended' || (state === null && (raw.ended === true || row.pill === 'Ended'));
  const muted = ended || Boolean(row.pill);
  const endedBy = text(raw.endedBy);
  const facts: CertificateCardFact[] = [];
  const reason = text(raw.exemptionReason);
  if (reason) facts.push({ label: t('certificate.card.reason'), value: reason, wide: true });
  const form = text(raw.formType);
  if (form) facts.push({ label: t('certificate.card.form'), value: form });
  if (effectiveFrom) facts.push({ label: t('certificate.card.effective'), value: formatDateOnly(effectiveFrom, format) });
  facts.push({ label: t('certificate.card.expires'), value: expiresOn ? formatDateOnly(expiresOn, format) : t('certificate.card.noExpiry') });
  if (ended && row.at) facts.push({ label: t('certificate.card.ended'), value: endedBy ? `${when(row.at)} ${t('certificate.card.by')} ${endedBy}` : when(row.at), wide: true });
  const badge = state === 'inForce' && !muted && code && effectiveFrom ? taxExemptBadges([{ stateCode: code, effectiveFrom, expiresOn }], format)[0] ?? null : null;
  return {
    title: municipality ? `${place} · ${municipality}` : place,
    badge,
    pill: row.pill ? { text: row.pill, group: row.pillGroup ?? 'inactive' } : null,
    muted,
    ended,
    facts,
  };
}
